// src/App.js
import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { supabase } from "./supabaseClient";
import SignIn from "./SignIn";
import UsernamePicker from "./UsernamePicker";
import MainApp from "./MainApp";

const App = () => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  // Helper to fetch user profile from Supabase
  const fetchUserProfile = async (currentUser) => {
    if (!currentUser) {
      setProfile(null);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", currentUser.id)
        .maybeSingle();

      if (!error && data) {
        setProfile(data);
      } else {
        setProfile(null);
      }
    } catch (err) {
      console.error("Error fetching profile:", err);
      setProfile(null);
    }
  };

  useEffect(() => {
    // 1. Fetch initial session on app load
    supabase.auth.getSession().then(({ data: { session } }) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      if (currentUser) {
        fetchUserProfile(currentUser).finally(() => setLoadingUser(false));
      } else {
        setLoadingUser(false);
      }
    });

    // 2. Listen to active auth state changes (login, logout, OAuth callback)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      if (currentUser) {
        await fetchUserProfile(currentUser);
      } else {
        setProfile(null);
      }
      setLoadingUser(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Supabase Google OAuth Login
  const loginWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.origin,
        },
      });
      if (error) console.error("Google Auth error:", error.message);
    } catch (err) {
      console.error("Failed to initiate Google login:", err);
    }
  };

  // Centered loading state while Supabase Auth initializes
  if (loadingUser) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          justifyContent: "center",
          alignItems: "center",
          fontFamily: "system-ui, -apple-system, sans-serif",
          backgroundColor: "#f9fafb",
          color: "#4b5563",
        }}
      >
        <div
          style={{
            width: "36px",
            height: "36px",
            border: "3px solid #e5e7eb",
            borderTop: "3px solid #2563eb",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
            marginBottom: "1rem",
          }}
        />
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
        <p style={{ margin: 0, fontWeight: "500", fontSize: "0.95rem" }}>
          Initializing Session...
        </p>
      </div>
    );
  }

  // Determine if user has a valid configured username
  const username =
    profile?.username ||
    user?.user_metadata?.username ||
    user?.user_metadata?.full_name ||
    user?.displayName;

  const hasUsername = Boolean(username?.trim());

  // Unified user object mapped for backwards compatibility
  const activeUser = user
    ? {
        ...user,
        uid: user.id, // back-compat for components checking `user.uid`
        displayName: username,
        username: username,
      }
    : null;

  return (
    <Router>
      <Routes>
        <Route
          path="/"
          element={
            !activeUser ? (
              <SignIn signInWithGoogle={loginWithGoogle} />
            ) : !hasUsername ? (
              <UsernamePicker
                user={activeUser}
                onUsernameSet={(newUsername) => {
                  setProfile((prev) => ({ ...prev, username: newUsername }));
                  setUser((prev) => ({
                    ...prev,
                    user_metadata: {
                      ...prev?.user_metadata,
                      username: newUsername,
                    },
                  }));
                }}
              />
            ) : (
              <MainApp user={activeUser} />
            )
          }
        />

        {/* Redirect any unhandled route back to root */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

export default App;